import React from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

const DeleteDepartmentDialog = ({
  open,
  department,
  users = [],
  usersLoading = false,
  usersError = "",
  submitting = false,
  onClose,
  onSubmit,
}) => {
  const hasUsers = users.length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Delete department</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: 420 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Do you want to delete department <strong>{department?.name}</strong>?
        </Typography>
        {usersLoading ? (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={28} color="secondary" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Checking for associated users…
            </Typography>
          </Stack>
        ) : null}
        {usersError ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {usersError} You can still try to delete if the department has no users.
          </Alert>
        ) : null}
        {!usersLoading && hasUsers ? (
          <>
            <Alert severity="warning" sx={{ mb: 1 }}>
              This department has {users.length} associated user
              {users.length === 1 ? "" : "s"}. Reassign or remove them before deleting.
            </Alert>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Associated users
            </Typography>
            <List dense disablePadding sx={{ maxHeight: 220, overflow: "auto" }}>
              {users.map((u, idx) => (
                <React.Fragment key={u?.id ?? idx}>
                  {idx > 0 ? <Divider component="li" /> : null}
                  <ListItem alignItems="flex-start" sx={{ py: 0.75 }}>
                    <ListItemText
                      primary={u?.name || "—"}
                      secondary={[u?.username, u?.role].filter(Boolean).join(" · ") || undefined}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          color="error"
          variant="contained"
          onClick={onSubmit}
          loading={submitting}
          disabled={usersLoading || hasUsers}
        >
          Delete
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteDepartmentDialog;
